import { get, patch, post } from '../coreApi';

export type AttentionSeverity = 'critical' | 'warning' | 'info';

export interface AttentionAssignee {
  id: number;
  name: string;
  email?: string | null;
  avatarUrl?: string | null;
  kind?: 'user' | 'network_member' | string;
}

export interface AttentionMetadata {
  entityTitle?: string | null;
  entityDescription?: string | null;
  status?: string | null;
  urgency?: string | null;
  priority?: string | null;
  taskType?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  lastActivityAt?: string | null;
  deadlineAt?: string | null;
  assignees?: AttentionAssignee[];
  [key: string]: unknown;
}

export interface AttentionSignal {
  type: string;
  severity: AttentionSeverity;
  score: number;
  title: string;
  description?: string | null;
  entityType?: string;
  entityId?: string | number;
  instanceId?: string | number | null;
  detectedAt?: string;
  reasons?: string[];
  metadata?: AttentionMetadata;
}

export interface DailyTicketPoint {
  date: string;
  created: number;
  completed: number;
}

export interface DailyNetworkPoint {
  date: string;
  requests: number;
  completedAssignments: number;
}

export interface PeriodComparison {
  current: number;
  previous: number;
  percentChange: number | null;
}

export interface PlatformSystemOverview {
  generatedAt: string;
  period: { days: number };
  attention: AttentionSignal[];
  tickets: {
    open: number;
    emergencyOpen: number;
    unassignedOpen: number;
    completedInPeriod: number;
    medianOpenAgeHours: number | null;
    statusDistribution: Record<string, number>;
    daily: DailyTicketPoint[];
    comparison?: PeriodComparison;
  };
  coreNetwork: {
    activeMembers: number;
    openRequests: number;
    unfilledRequests: number;
    offersInPeriod: number;
    assignmentsInPeriod: number;
    completedAssignmentsInPeriod: number;
    medianHoursToFirstOffer: number | null;
    medianHoursToAssignment: number | null;
    daily: DailyNetworkPoint[];
    comparison?: PeriodComparison;
  };
}

export interface SiteHealthItem {
  key: string;
  name: string;
  url: string;
  finalUrl: string | null;
  status: 'healthy' | 'degraded' | 'down';
  statusCode: number | null;
  responseTimeMs: number;
  checkedAt: string;
  error: string | null;
}

export interface PlatformSiteHealth {
  checkedAt: string;
  summary: {
    total: number;
    healthy: number;
    degraded: number;
    down: number;
    overallStatus: 'healthy' | 'degraded' | 'down';
  };
  sites: SiteHealthItem[];
}

export interface SiteHealthHistoryPoint {
  checkedAt: string;
  uptimePercent: number;
  averageResponseTimeMs: number | null;
  checks: number;
}

export interface SiteHealthHistoryItem {
  key: string;
  name: string;
  url: string;
  latestStatus: 'healthy' | 'degraded' | 'down' | null;
  latestCheckedAt: string | null;
  uptime: {
    last24Hours: number | null;
    last7Days: number | null;
    last30Days: number | null;
  };
  series: SiteHealthHistoryPoint[];
}

export interface PlatformSiteHealthHistory {
  generatedAt: string;
  period: { days: number; start: string; end: string };
  sites: SiteHealthHistoryItem[];
}

export type PlatformUpdateType =
  | 'update'
  | 'feature'
  | 'bug_fix'
  | 'release'
  | 'deployment'
  | 'maintenance'
  | 'incident'
  | 'security';

export interface PlatformUpdate {
  id: number | string;
  title: string;
  summary?: string | null;
  type?: PlatformUpdateType | string | null;
  status?: 'draft' | 'published' | 'archived' | string | null;
  version?: string | null;
  pullRequestNumber?: number | null;
  gitCommitSha?: string | null;
  publishedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlatformUpdateInput {
  title: string;
  summary?: string | null;
  type?: PlatformUpdateType;
  version?: string | null;
  pullRequestNumber?: number | null;
  gitCommitSha?: string | null;
}

interface OverviewResponse {
  success: boolean;
  overview: PlatformSystemOverview;
}

interface HealthResponse {
  success: boolean;
  health: PlatformSiteHealth;
}

interface HealthHistoryResponse {
  success: boolean;
  history: PlatformSiteHealthHistory;
}

interface UpdatesResponse {
  success: boolean;
  updates?: PlatformUpdate[];
  rows?: PlatformUpdate[];
}

interface UpdateResponse {
  success: boolean;
  update: PlatformUpdate;
}

export const getSystemOverview = async (days = 30): Promise<PlatformSystemOverview> => {
  const response = await get<OverviewResponse>('/api/platform/system/overview', {
    params: { days },
  });
  return response.overview;
};

export const getSiteHealth = async (): Promise<PlatformSiteHealth> => {
  const response = await get<HealthResponse>('/api/platform/system/site-health');
  return response.health;
};

export const getSiteHealthHistory = async (days = 7): Promise<PlatformSiteHealthHistory> => {
  const response = await get<HealthHistoryResponse>('/api/platform/system/site-health/history', {
    params: { days },
  });
  return response.history;
};

export const getPlatformUpdates = async (limit = 8): Promise<PlatformUpdate[]> => {
  const response = await get<UpdatesResponse>('/api/platform/system/updates', {
    params: { limit, status: 'published' },
  });
  return response.updates || response.rows || [];
};

export const getPlatformUpdateDrafts = async (limit = 50): Promise<PlatformUpdate[]> => {
  const response = await get<UpdatesResponse>('/api/platform/system/updates', {
    params: { limit, status: 'draft' },
  });
  return response.updates || response.rows || [];
};

export const createPlatformUpdate = async (input: PlatformUpdateInput): Promise<PlatformUpdate> => {
  const response = await post<UpdateResponse>('/api/platform/system/updates', input);
  return response.update;
};

export const updatePlatformUpdate = async (
  id: PlatformUpdate['id'],
  input: Partial<PlatformUpdateInput>
): Promise<PlatformUpdate> => {
  const response = await patch<UpdateResponse>(`/api/platform/system/updates/${id}`, input);
  return response.update;
};

export const publishPlatformUpdate = async (id: PlatformUpdate['id']): Promise<PlatformUpdate> => {
  const response = await post<UpdateResponse>(`/api/platform/system/updates/${id}/publish`, {});
  return response.update;
};

export const archivePlatformUpdate = async (id: PlatformUpdate['id']): Promise<PlatformUpdate> => {
  const response = await post<UpdateResponse>(`/api/platform/system/updates/${id}/archive`, {});
  return response.update;
};
